import React from 'react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { OperatingSystemBanner } from '../components/brand/OperatingSystemBanner';
import { Seo } from '../components/seo/Seo';
import { SERVICE_LIST } from '../lib/serviceData';
import { organizationSchema, breadcrumbSchema } from '../lib/seoSchemas';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Activity, Heart, Video, Home, Stethoscope, Brain, 
  Moon, Sun, CheckCircle, ArrowRight, Users, Clock,
  Shield, Calendar, MessageSquare, Pill
} from 'lucide-react';

export default function ServicesPage() {
  const { theme, toggleTheme } = useTheme();

  const mainServices = [
    {
      icon: Home,
      title: 'In-Home Care',
      description: 'Comprehensive care services delivered in the comfort of your home by verified healthcare professionals.',
      features: [
        'Personal care assistance (bathing, grooming, dressing)',
        'Medication management and reminders',
        'Meal preparation and nutrition support',
        'Light housekeeping and errands',
        'Companionship and emotional support',
        'Mobility assistance and fall prevention'
      ],
      color: 'blue'
    },
    {
      icon: Video,
      title: 'Telehealth Services',
      description: 'Secure video visits with licensed clinicians from anywhere, anytime.',
      features: [
        'Virtual visits with RNs and NPs',
        'Medication review and adjustments',
        'Care plan monitoring and updates',
        'Mental health check-ins',
        'Post-visit documentation (SOAP notes)',
        'Family conference calls'
      ],
      color: 'blue'
    },
    {
      icon: Stethoscope,
      title: 'Skilled Nursing',
      description: 'Clinical care from licensed nurses for complex medical needs.',
      features: [
        'Wound care and dressing changes',
        'IV therapy and infusions',
        'Vital signs monitoring',
        'Chronic disease management',
        'Post-surgical care',
        'Catheter and ostomy care'
      ],
      color: 'purple'
    },
    {
      icon: Heart,
      title: 'Hospice & Palliative Care',
      description: 'Compassionate end-of-life care focused on comfort and dignity.',
      features: [
        'Pain and symptom management',
        'Emotional and spiritual support',
        'Family caregiver support',
        'Respite care services',
        'Bereavement counseling',
        'On-call visit coordination'
      ],
      color: 'rose'
    },
    {
      icon: Brain,
      title: 'Cognitive Care',
      description: 'Specialized care for dementia, Alzheimer\'s, and cognitive conditions.',
      features: [
        'Memory care activities',
        'Behavior management',
        'Safety monitoring',
        'Routine and structure support',
        'Communication techniques',
        'Family education and support'
      ],
      color: 'orange'
    },
    {
      icon: Users,
      title: 'Family Caregiver Support',
      description: 'Resources and relief for family members providing care.',
      features: [
        'Respite care scheduling',
        'Caregiver training',
        'Care coordination',
        'Family portal access',
        'Support group connections',
        'Burnout prevention resources'
      ],
      color: 'green'
    }
  ];

  const additionalServices = [
    { name: 'Physical Therapy', icon: Activity },
    { name: 'Occupational Therapy', icon: Stethoscope },
    { name: 'Speech Therapy', icon: MessageSquare },
    { name: 'Medication Reconciliation', icon: Pill },
    { name: 'Care Plan Management', icon: Calendar },
    { name: 'Insurance Coordination', icon: Shield }
  ];

  // Precision/abstract clinical imagery per service tile (Swiss-bank aesthetic)
  const SERVICE_IMAGES = [
    'https://images.pexels.com/photos/7108116/pexels-photo-7108116.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=420&w=720',
    'https://images.pexels.com/photos/36764709/pexels-photo-36764709.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=420&w=720',
    'https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHRleHR1cmUlMjB3aGl0ZSUyMGdyZXl8ZW58MHx8fHwxNzgxMDY5NjE1fDA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1770117663190-046672d8eaa8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxtYWNybyUyMG1lZGljYWwlMjBpbnN0cnVtZW50JTIwcHJlY2lzaW9ufGVufDB8fHx8MTc4MTA2OTYyNHww&ixlib=rb-4.1.0&q=85',
    'https://images.pexels.com/photos/7108116/pexels-photo-7108116.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=420&w=720',
    'https://images.pexels.com/photos/36764709/pexels-photo-36764709.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=420&w=720',
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-cn-ink-900">
      <Seo
        title="Care services on the post-acute OS | CaraLoom"
        description="Every post-acute service line — private duty, skilled nursing, hospice, memory care, home health aides — orchestrated end-to-end on the CaraLoom operating system for the hospitals, agencies, and independent clinicians who deliver it."
        path="/services"
        keywords={['post-acute operating system', 'home health software', 'skilled nursing platform', 'private duty nursing platform', 'agency software']}
        schema={[organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Care services', path: '/services' }])]}
      />
      <PublicNavbar />
      <OperatingSystemBanner />

      {/* Hero with imagery */}
      <section className="pt-12 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cn-bone-100 via-white to-cn-bone-200 dark:from-cn-ink-900 dark:via-cn-ink-900 dark:to-cn-ink-800">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="cn-eyebrow mb-3">Care services on the post-acute OS</div>
            <h1 className="cn-display text-4xl sm:text-5xl lg:text-6xl text-cn-text mb-6">
              Every post-acute service line, orchestrated.
            </h1>
            <p className="text-lg text-cn-text-muted leading-relaxed max-w-xl mb-6">
              Hospitals, home-health agencies, hospice teams, and independent clinicians
              run their delivery on CaraLoom. Families come along for the ride —
              visit-by-visit, in real time.
            </p>
            <div className="flex flex-wrap gap-3" data-testid="services-hero-ctas">
              <Link to="/pricing" className="cn-btn cn-btn-primary" data-testid="services-hero-pricing">
                See operator pricing <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/auth" className="cn-btn cn-btn-ghost" data-testid="services-hero-get-started">
                Get matched as a family <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[360px] md:h-[440px]">
              <div className="col-span-6 row-span-4 rounded-3xl overflow-hidden cn-card">
                <img src="https://images.pexels.com/photos/7108116/pexels-photo-7108116.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=700&w=900" alt="Precision instruments" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-3 row-span-2 rounded-2xl overflow-hidden cn-card">
                <img src="https://images.pexels.com/photos/36764709/pexels-photo-36764709.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=350&w=450" alt="Macro precision blades" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-3 row-span-2 rounded-2xl overflow-hidden cn-card">
                <img src="https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHRleHR1cmUlMjB3aGl0ZSUyMGdyZXl8ZW58MHx8fHwxNzgxMDY5NjE1fDA&ixlib=rb-4.1.0&q=85" alt="Abstract clinical texture" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainServices.map((service, index) => (
              <Card key={index} className="bg-white dark:bg-cn-ink-800 border border-cn-border shadow-cn-subtle hover:shadow-cn-floating transition-shadow overflow-hidden">
                <div className="aspect-[16/10] bg-cn-bone-200 overflow-hidden">
                  <img
                    src={SERVICE_IMAGES[index % SERVICE_IMAGES.length]}
                    alt={service.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-cn-sage-100 rounded-2xl flex items-center justify-center mb-4 -mt-12 relative bg-white border border-cn-border shadow-cn-subtle">
                    <service.icon className="w-6 h-6 text-cn-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white font-display mb-2">{service.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-cn-primary dark:text-blue-400 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600 dark:text-slate-400">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-cn-ink-800/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white font-display mb-12 text-center">
            Additional Services
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {additionalServices.map((service, index) => (
              <div key={index} className="flex flex-col items-center p-6 bg-white dark:bg-cn-ink-800 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-cn-sage-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mb-3">
                  <service.icon className="w-6 h-6 text-cn-primary dark:text-blue-400" />
                </div>
                <span className="text-sm font-medium text-slate-900 dark:text-white text-center">{service.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How the OS orchestrates this — dual audience (operator + family) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="services-how-os-orchestrates">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="cn-eyebrow mb-3">For agencies, hospitals, and clinicians</div>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white font-display mb-6">
                How the post-acute OS orchestrates each service line
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">
                Six product suites, one HIPAA-ready stack — wired so a hospital
                discharge plan becomes a staffed home-health episode in hours, not days.
              </p>
              <div className="space-y-4" data-testid="services-suite-list">
                {[
                  { name: 'Referrals', desc: 'FHIR + faxed referral intake, AI triage, hospital\u2192agency handoff in minutes.', href: '/suites/referrals' },
                  { name: 'Workforce', desc: 'Open-shift broadcast, credentialing, CEU library, paid-per-visit clinicians.', href: '/suites/workforce' },
                  { name: 'Operations', desc: 'Census, A/R aging, P&L, executive dashboard, multi-location view.', href: '/suites/operations' },
                  { name: 'Compliance', desc: 'SOC 2 evidence, BAA chain, audit trails, payer-VBC contracts.', href: '/suites/compliance' },
                  { name: 'Revenue Cycle', desc: 'AI scrub, denial prediction, 837P/835, family auto-statements.', href: '/suites/revenue-cycle' },
                  { name: 'Care', desc: 'AI-assisted visit notes, telehealth summaries, family Care-Hub digest.', href: '/suites/care' },
                ].map((s) => (
                  <Link key={s.name} to={s.href} className="cn-card cn-card-hover p-4 rounded-2xl block" data-testid={`services-suite-${s.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="flex items-start gap-3">
                      <div className="font-semibold text-cn-text dark:text-white text-sm min-w-[110px]">{s.name}</div>
                      <p className="text-sm text-cn-text-muted flex-1 leading-relaxed">{s.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            <div className="bg-cn-sage-50 dark:bg-blue-900/20 rounded-3xl p-8">
              <div className="cn-eyebrow mb-3">For families on the receiving end</div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white font-display mb-6">
                What you get when your care team runs on CaraLoom
              </h3>
              <ul className="space-y-4">
                {[
                  'One Care-Hub for every visit, note, and bill \u2014 not 4 patient portals.',
                  'Verified, license-checked clinicians \u2014 background-screened before any visit.',
                  'Real-time family digest after every visit, so distant relatives stay in the loop.',
                  'Transparent billing with one-click pay and automatic family statements.',
                  'AI-assisted clinical notes shared with your physician and family circle.',
                  'HIPAA-ready messaging \u2014 secure conversations with your care team.',
                  'Faster matches because intake, credentialing, and scheduling live on one stack.',
                  'Telehealth visits and in-person care coordinated in the same record.',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-cn-primary dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 dark:text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Dedicated service landing pages (internal linking for SEO) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="dedicated-services">
        <div className="max-w-7xl mx-auto">
          <div className="cn-eyebrow mb-3">Explore in depth</div>
          <h2 className="cn-display text-3xl text-cn-text mb-3">Our specialized care services</h2>
          <p className="text-cn-text-muted max-w-2xl mb-10">Learn more about each service, what's included, and how CaraLoom matches you with the right verified clinician near you.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICE_LIST.map((s) => {
              const SIcon = s.icon;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="cn-card cn-card-hover p-6 rounded-3xl block" data-testid={`services-page-link-${s.slug}`}>
                  <div className="w-11 h-11 rounded-xl bg-cn-soft flex items-center justify-center mb-3">
                    <SIcon className="w-5 h-5 text-cn-primary" />
                  </div>
                  <h3 className="font-semibold text-lg text-cn-text mb-1">{s.name}</h3>
                  <p className="text-sm text-cn-text-muted mb-3 line-clamp-2">{s.eyebrow}.</p>
                  <span className="text-sm text-cn-accent font-medium inline-flex items-center gap-1">Learn more <ArrowRight className="w-3.5 h-3.5" /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cn-primary to-cn-sage-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white font-display mb-4">
            Run your service line on the post-acute OS
          </h2>
          <p className="text-lg text-cn-sage-100 mb-8">
            Operators get one HIPAA-ready stack for referrals, workforce, RCM, compliance, and family
            communication. Families get one Care-Hub for every visit, note, and bill.
          </p>
          <div className="flex flex-wrap gap-3 justify-center" data-testid="services-final-ctas">
            <Link to="/pricing" data-testid="services-final-pricing">
              <Button className="bg-white text-cn-primary hover:bg-cn-sage-50 rounded-full px-10 h-14 text-lg font-medium">
                See operator pricing
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/auth" data-testid="services-final-get-started">
              <Button className="bg-white/10 text-white border border-white/40 hover:bg-white/20 rounded-full px-10 h-14 text-lg font-medium">
                Get matched as a family
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
